import { HelpCircle, Plus } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { useEditorActions } from '@/store/editor.store';

interface Props {
  /** The 1-based index of this group across all quiz sets on the page —
   *  shown in the header for orientation. */
  setIndex: number;
  /** Beat id of the last quiz slide in the run, used as `afterSlideId`
   *  when "Add another question" is clicked. */
  lastSlideId: string;
  /** Total questions in this set, used for the "N of M" label. */
  total: number;
  /** Renders each slide pre-wrapped with its position label. */
  children: ReactNode;
}

/**
 * Visual wrapper around a run of consecutive `kind:"quiz"` slides. The data
 * model stays a chain of single-question quiz beats (SCHEMA_USAGE.md Rule 4),
 * but the teacher sees "one quiz with several questions" with an
 * "Add another question" CTA at the bottom of the group.
 */
export function QuizSetGroup({ setIndex, lastSlideId, total, children }: Props) {
  const actions = useEditorActions();

  const single = total === 1;
  return (
    <section className="relative rounded-2xl border-2 border-dashed border-amber-300/70 bg-amber-50/40 p-4">
      <header className="mb-3 flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-md bg-amber-200/80 text-amber-700">
          <HelpCircle size={13} />
        </span>
        <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-amber-800">
          Quiz set {setIndex}
        </span>
        <span className="text-[11px] text-amber-700/80">
          {total} question{single ? '' : 's'} · single-question quiz beats chained together
        </span>
      </header>

      <div className="flex flex-col gap-6">{children}</div>

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          onClick={() => actions.insertQuizSlide({ afterSlideId: lastSlideId })}
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-amber-400 bg-white px-4 py-1.5 text-[12px] font-semibold text-amber-700 transition hover:border-amber-500 hover:bg-amber-100"
        >
          <Plus size={12} /> Add another question
        </button>
      </div>
    </section>
  );
}

/** Label rendered above each slide inside a quiz set. */
export function QuizSetSlideHeader({
  position,
  total,
}: {
  position: number;
  total: number;
}) {
  return (
    <div className="-mb-3 flex items-center gap-2 px-1">
      <span className="rounded-md bg-amber-200/80 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-amber-800">
        Question {position} of {total}
      </span>
    </div>
  );
}

/** No-op fragment used to render children — kept here so CanvasContainer
 *  can wrap each grouped child without unconditional JSX clutter. */
export const QuizSetSlideWrap = ({ children }: { children: ReactNode }) => (
  <Fragment>{children}</Fragment>
);
